import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';
import type { TrialFormData } from '../types';

interface TrialModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const TrialModal: React.FC<TrialModalProps> = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState<TrialFormData>({
        name: '',
        email: '',
        phone: '',
        classType: 'music',
        ageGroup: '',
        experience: 'beginner',
        preferredTime: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errors, setErrors] = useState<Partial<TrialFormData>>({});

    const courses = [
        { id: 'music', name: 'Music (Hindustani Vocals, Tabla)' },
        { id: 'yoga', name: 'Yoga (Hatha, Vinyasa)' },
        { id: 'dance', name: 'Dance (Bharatanatyam, Bollywood)' }
    ];

    const experienceLevels = [
        { id: 'beginner', name: 'Complete Beginner' },
        { id: 'intermediate', name: 'Intermediate' },
        { id: 'advanced', name: 'Advanced' }
    ];

    const timeSlots = [
        { id: 'morning', name: 'Morning (6 AM - 12 PM IST)' },
        { id: 'afternoon', name: 'Afternoon (12 PM - 6 PM IST)' },
        { id: 'evening', name: 'Evening (6 PM - 10 PM IST)' }
    ];

    const validateForm = (): boolean => {
        const newErrors: Partial<TrialFormData> = {};

        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (formData.phone && !/^\+?[\d\s-()]+$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }
        if (!formData.ageGroup.trim()) newErrors.ageGroup = 'Age group is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            // For demo purposes, simulate API call without actually calling it
            // This ensures the landing page works without a backend
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Optional: Try to submit to API in background
            try {
                await apiService.submitTrialForm(formData);
                console.log('Form submitted to API successfully');
            } catch (apiError) {
                console.log('API not available, form data logged locally:', formData);
                // Store form data locally for later processing
                localStorage.setItem('pendingTrialSignup', JSON.stringify({
                    ...formData,
                    timestamp: new Date().toISOString()
                }));
            }

            setIsSuccess(true);

            // Track conversion
            try {
                if (typeof window !== 'undefined' && (window as any).gtag) {
                    (window as any).gtag('event', 'trial_signup', {
                        event_category: 'conversion',
                        event_label: formData.classType,
                        value: 1
                    });
                }
            } catch (trackingError) {
                console.log('Analytics not available:', trackingError);
            }
        } catch (error) {
            console.error('Trial signup error:', error);
            alert('Something went wrong. Please try again or contact support.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (field: keyof TrialFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData({
                name: '',
                email: '',
                phone: '',
                classType: 'music',
                ageGroup: '',
                experience: 'beginner',
                preferredTime: '',
                message: ''
            });
            setErrors({});
            setIsSuccess(false);
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={handleClose}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isSuccess ? (
                            // Success State
                            <div className="p-8 text-center">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.2, type: "spring", damping: 15 }}
                                    className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                                >
                                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </motion.div>
                                <h3 className="font-cinzel text-2xl font-bold text-gray-900 mb-4">
                                    Welcome to SwarSadhana! 🎉
                                </h3>
                                <p className="font-poppins text-gray-600 mb-6">
                                    Thank you for signing up for your free trial! Our team will contact you within 24 hours to schedule your first class.
                                </p>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={handleClose}
                                    className="bg-brand-gold text-gray-900 px-6 py-2 rounded-lg font-poppins font-semibold hover:bg-yellow-500 transition-colors"
                                >
                                    Continue Exploring
                                </motion.button>
                            </div>
                        ) : (
                            // Form State
                            <>
                                <div className="p-6 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <img
                                                src="/images/swarsadhana-logo-animated.svg"
                                                alt="SwarSadhana Logo"
                                                className="h-10 w-10"
                                            />
                                            <div>
                                                <h2 className="font-cinzel text-2xl font-bold text-gray-900">
                                                    Start Your Free Trial
                                                </h2>
                                                <p className="font-poppins text-gray-600 text-sm">
                                                    Experience the harmony of personalized learning
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleClose}
                                            className="text-gray-400 hover:text-gray-600 transition-colors"
                                            disabled={isSubmitting}
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Name */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.name ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Enter your full name"
                                                disabled={isSubmitting}
                                            />
                                            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                                        </div>

                                        {/* Age Group */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Age Group *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.ageGroup}
                                                onChange={(e) => handleInputChange('ageGroup', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.ageGroup ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="e.g., 4-7, 25-35, 60+"
                                                disabled={isSubmitting}
                                            />
                                            {errors.ageGroup && <p className="text-red-500 text-sm mt-1">{errors.ageGroup}</p>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Email */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="your@email.com"
                                                disabled={isSubmitting}
                                            />
                                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Phone Number (Optional)
                                            </label>
                                            <input
                                                type="tel"
                                                value={formData.phone || ''}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.phone ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="+91 9876543210"
                                                disabled={isSubmitting}
                                            />
                                            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                                        </div>
                                    </div>

                                    {/* Course Selection */}
                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Choose Your Course *
                                        </label>
                                        <select
                                            value={formData.classType}
                                            onChange={(e) => handleInputChange('classType', e.target.value as 'music' | 'yoga' | 'dance')}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all"
                                            disabled={isSubmitting}
                                        >
                                            {courses.map(course => (
                                                <option key={course.id} value={course.id}>{course.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Experience Level */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Experience Level *
                                            </label>
                                            <select
                                                value={formData.experience}
                                                onChange={(e) => handleInputChange('experience', e.target.value as 'beginner' | 'intermediate' | 'advanced')}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all"
                                                disabled={isSubmitting}
                                            >
                                                {experienceLevels.map(level => (
                                                    <option key={level.id} value={level.id}>{level.name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Preferred Time */}
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Preferred Time (Optional)
                                            </label>
                                            <select
                                                value={formData.preferredTime || ''}
                                                onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all"
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Select preferred time</option>
                                                {timeSlots.map(slot => (
                                                    <option key={slot.id} value={slot.id}>{slot.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Additional Message (Optional)
                                        </label>
                                        <textarea
                                            value={formData.message || ''}
                                            onChange={(e) => handleInputChange('message', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all resize-none"
                                            rows={3}
                                            placeholder="Tell us about your learning goals and what you hope to achieve..."
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    {/* Submit Button */}
                                    <motion.button
                                        type="submit"
                                        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                        disabled={isSubmitting}
                                        className={`w-full py-3 rounded-lg font-poppins font-semibold text-base transition-all ${isSubmitting
                                            ? 'bg-gray-400 cursor-not-allowed text-white'
                                            : 'bg-brand-gold hover:bg-yellow-500 hover:shadow-lg text-gray-900'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center justify-center">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Submitting...
                                            </div>
                                        ) : (
                                            'Start My Free Trial'
                                        )}
                                    </motion.button>

                                    <p className="text-center text-sm text-gray-500 font-poppins">
                                        By submitting, you agree to our Terms of Service and Privacy Policy.
                                        No payment required for your free trial.
                                    </p>
                                </form>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default TrialModal;
